import type { TProductCard } from '@chemist4u/newt-components';
import type { HttpTypes } from '@medusajs/types';
import { getProductPrice } from '../get-product-price';

function mapProductImages(
  product: HttpTypes.StoreProduct,
): TProductCard['images'] | undefined {
  if (!product.images || product.images.length === 0) {
    return undefined;
  }

  return product.images.map((img, idx) => ({
    ...productCardImageFromMedusaImage(product, img.url),
    id: `${product.id}-img-${idx}`,
  }));
}

function productCardImageFromMedusaImage(
  product: HttpTypes.StoreProduct,
  imageUrl: string | null = '',
): TProductCard['image'] {
  return {
    id: product.id ?? '',
    url: imageUrl ?? '',
    src: imageUrl ?? '',
    alt: product.title || 'Product image',
  };
}

function saleLabelFromPriceDiff(
  priceDiff: string | null | undefined,
): string | undefined {
  if (!priceDiff || priceDiff === '0') {
    return undefined;
  }
  return `${priceDiff}% Off`;
}

function compareAtPriceFromPriceDiff(
  priceDiff: string | null | undefined,
  originalPrice: string | null | undefined,
): string | undefined {
  if (!priceDiff || priceDiff === '0') {
    return undefined;
  }
  return originalPrice ?? undefined;
}

/**
 * Maps a Medusa StoreProduct to TProductCard props for the product slider.
 * Extracts pricing, images, and sale information for the carousel display.
 */
export function toSliderProductCard(
  product: HttpTypes.StoreProduct,
): TProductCard {
  const { cheapestPrice } = getProductPrice({ product });
  const href = `/${product.handle}`;

  const primaryImage = productCardImageFromMedusaImage(
    product,
    product.thumbnail,
  );

  const images = mapProductImages(product);
  const { calculated_price: price } = cheapestPrice || {};

  const compareAtPrice = compareAtPriceFromPriceDiff(
    cheapestPrice?.percentage_diff,
    cheapestPrice?.original_price,
  );

  const saleLabel = saleLabelFromPriceDiff(cheapestPrice?.percentage_diff);

  return {
    variant: 'compact',
    href,
    productName: product.title ?? 'Product',
    image: primaryImage,
    images,
    price,
    compareAtPrice,
    saleLabel,
    cta1: {
      label: 'View Product',
      href,
    },
    enableHoverPreview: true,
  };
}
